import { MsalService } from '@azure/msal-angular';
import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import {
  CentComUser, UserEvent, NavigationEvent, ClickEvent, AccessLevel
} from "@common";
import { environment } from 'src/environments/environment';
import { Observable, map } from 'rxjs';
import { PrimeIcons } from 'primeng/api';
import { MenuService } from 'src/app/layout/service/app.menu.service';

const adminMenu = {
  label: 'User Tracking',
  expanded: false,
  items: [
    {
      label: 'Dashboard',
      icon: `pi pi-fw ${PrimeIcons.CHART_BAR}`,
      routerLink: ['/user-tracking/']
    },
    {
      label: 'User Journeys',
      icon: 'pi pi-fw pi-filter',
      routerLink: ['/user-tracking/user-journeys']
    },
    {
      label: 'Heatmap Overlay',
      icon: 'pi pi-fw pi-map',
      routerLink: ['/user-tracking/heatmap']
    }
  ]
}

// Raw API response structure from DynamoDB
interface RawUserEventResponse {
  uuid?: string;
  createdAt?: number;
  userId?: string;
  sessionId?: string;
  eventType?: string;
  url?: string;
  eventDetails?: string;
  expiresAt?: number;
}

// Type guards for validation
function isRawUserEventResponse(item: RawUserEventResponse): item is RawUserEventResponse {
  return (
    typeof item === 'object' &&
    item !== null &&
    typeof item.userId === 'string' &&
    typeof item.sessionId === 'string' &&
    typeof item.url === 'string' &&
    typeof item.eventDetails === 'string' &&
    typeof item.eventType === 'string'
  );
}

function isNavigationEvent(args: any): args is NavigationEvent['args'] {
  return (
    typeof args === 'object' &&
    args !== null &&
    typeof args.from_url === 'string'
  );
}

function isClickEvent(args: any): args is ClickEvent['args'] {
  return (
    typeof args === 'object' &&
    args !== null &&
    typeof args.x === 'number' &&
    typeof args.y === 'number'
  );
}

// Transform raw API response to UserEvent
function transformToUserEvent(raw: RawUserEventResponse): UserEvent | null {
  if (!isRawUserEventResponse(raw)) {
    return null;
  }

  try {
    const eventDetails = JSON.parse(raw.eventDetails!);
    const user: CentComUser = {
      username: raw.userId!,
      isAdmin: false, // This field is not stored in DynamoDB, defaulting to false
    };

    // Determine event type based on eventType and args structure
    if (raw.eventType === 'navigate' && isNavigationEvent(eventDetails)) {
      return {
        user,
        url: raw.url!,
        sessionId: raw.sessionId!,
        args: eventDetails,
      } as NavigationEvent;
    } else if (raw.eventType === 'click' && isClickEvent(eventDetails)) {
      return {
        user,
        url: raw.url!,
        sessionId: raw.sessionId!,
        args: eventDetails,
      } as ClickEvent;
    } else {
      console.warn('Unknown event type or invalid args:', raw.eventType, eventDetails);
      return null;
    }
  } catch (error) {
    console.error('Error parsing eventDetails:', error, raw);
    return null;
  }
}

@Injectable({
  providedIn: 'root'
})
export class UserTrackingService {
  private endpoint = '/api/user-mgmt';

  private prevUrl = "";
  private userData?: CentComUser
  private env = environment.production ? "prod" : "test"

  constructor(
    private http: HttpClient,
    private msalService: MsalService,
    private menuService: MenuService
  ) { }

  updateUserInfo(): void {
    const username = this.msalService.instance.getActiveAccount()?.username;
    if (!username) {
      return;
    }
    this.http.get<AccessLevel>(`${this.endpoint}/access-level?username=${username}`).subscribe((userLevel) => {
      this.userData = {
        username,
        isAdmin: userLevel >= AccessLevel.Admin
      };
      this.menuService.menuModel.push(adminMenu);
    });
  }

  getEvents(type: "navigate", args?: { [key: string]: string }): Observable<NavigationEvent[]>;
  getEvents(type: "click", args?: { [key: string]: string }): Observable<ClickEvent[]>;
  getEvents(type: "navigate" | "click", args?: { [key: string]: string }): Observable<UserEvent[]> {
    let params = new HttpParams();
    params = params.append("environment", String(this.env))
    if (args) {
      for (const [key, val] of Object.entries(args)) {
        params = params.append(key, val)
      }
    }
    return this.http.get<RawUserEventResponse[]>(`${this.endpoint}/event/type/${type}`, { params }).pipe(
      map((rawEvents) => {
        return rawEvents
          .map(transformToUserEvent)
          .filter((event): event is UserEvent => event !== null);
      })
    );
  }

  logRoute(newUrl: string): void {
    if (!this.userData) {
      this.prevUrl = newUrl;
      console.warn('Skipping navigation log until user data is available.');
      return;
    }
    this._postEvent("navigate", {
      user: this.userData,
      url: newUrl,
      sessionId: sessionStorage.getItem("session_id")!,
      args: { from_url: this.prevUrl }
    }).subscribe({
      error: (error: HttpErrorResponse) => {
        console.log(error)
      }
    })
    this.prevUrl = newUrl
  }

  logClick(event: MouseEvent): void {
    if (!this.userData) {
      console.warn('Skipping click log until user data is available.');
      return;
    }
    this._postEvent("click", {
      user: this.userData,
      url: this.prevUrl,
      sessionId: sessionStorage.getItem("session_id")!,
      args: {
        x: Math.round(event.pageX / event.view!.innerWidth * 10000) / 100,
        y: Math.round(event.pageY / event.view!.innerHeight * 10000) / 100
      }
    }).subscribe({
      error: (error: HttpErrorResponse) => {
        console.log(error)
      }
    });
  }

  _postEvent(type: "navigate" | "click", event: UserEvent): Observable<void> {
    let params = new HttpParams();
    params = params.append("environment", String(this.env))
    return this.http.post<void>(`${this.endpoint}/${type}`, event, { params })
  }
}
